import Vue from "vue";
import Vuex from "vuex";
import { defaultLocale, LOCALE_STORAGE_KEY } from "@/i18n/localeConfig";

import {
  getlogin_pc,
  getuser,
  getbalance,
  getapp,
} from "@/api/index";

Vue.use(Vuex);

function readJsonStorage(key, fallback = null) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") || fallback;
  } catch (error) {
    localStorage.removeItem(key);
    return fallback;
  }
}

const store = new Vuex.Store({
  state: {
    token: readJsonStorage("token"),
    userInfo: readJsonStorage("userInfo"),
    balance: localStorage.getItem("balance") || "null",
    appInfo: JSON.parse(localStorage.getItem("appInfo") || "null"),
    locale: localStorage.getItem(LOCALE_STORAGE_KEY) || defaultLocale,
  },
  mutations: {
    setLocale(state, locale) {
      state.locale = locale;
    },
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
    },
    setToken(state, token) {
      state.token = token;
    },
    setBalance(state, balance) {
      state.balance = balance;
    },
    setAppInfo(state, appInfo) {
      state.appInfo = appInfo;
    },
  },
  actions: {
    login(context, userInfo) {
      return new Promise(async (resolve, reject) => {
        try {
          // 新登录必须从干净会话开始，避免旧 token 影响登录接口或后续请求。
          context.commit("setToken", null);
          context.commit("setUserInfo", null);
          context.commit("setBalance", null);
          localStorage.removeItem("token");
          localStorage.removeItem("userInfo");
          localStorage.removeItem("balance");

          const res = await getlogin_pc(userInfo);
          const token = res?.data?.api_token;
          if (!token) throw new Error("登录接口未返回 api_token");

          // 仅在内存中临时使用 token 获取用户资料，资料成功后再持久化完整会话。
          context.commit("setToken", token);
          const userRes = await getuser();
          if (!userRes?.data) throw new Error("用户资料获取失败");

          context.commit("setUserInfo", userRes.data);
          localStorage.setItem("token", JSON.stringify(token));
          localStorage.setItem("userInfo", JSON.stringify(userRes.data));
          context.dispatch("getBalance").catch(() => {});
          resolve();
        } catch (error) {
          context.dispatch("removeLoginInfo");
          reject(error);
        }
      });
    },
    async restoreSession({ state, commit, dispatch }) {
      if (!state.token) return false;

      try {
        const userRes = await getuser();
        if (!userRes?.data) throw new Error("用户资料获取失败");
        commit("setUserInfo", userRes.data);
        localStorage.setItem("userInfo", JSON.stringify(userRes.data));
        return true;
      } catch (error) {
        await dispatch("removeLoginInfo");
        return false;
      }
    },
    /** 本地退出：不调接口，保留 appInfo 等应用缓存 */
    logout({ dispatch }) {
      return dispatch("removeLoginInfo");
    },

    removeLoginInfo({ commit, state }) {
      localStorage.removeItem("userInfo");
      localStorage.removeItem("token");
      localStorage.removeItem("balance");
      localStorage.removeItem("GAME_FAVORITES");
      commit("setUserInfo", null);
      commit("setToken", null);
      commit("setBalance", null);
      // 保留 appInfo、语言、记住密码等缓存，供登录页客服等功能使用
      if (state.appInfo) {
        commit("setAppInfo", state.appInfo);
      }
    },
    getBalance({ commit }) {
      return new Promise((resolve, reject) => {
        getbalance()
          .then((res) => {
            commit("setBalance", res.data.balance);
            localStorage.setItem("balance", res.data.balance);
            resolve(res.data.balance);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    getAppInfo({ commit }) {
      return new Promise(async (resolve, reject) => {
        const res = await getapp();
        localStorage.setItem("appInfo", JSON.stringify(res.data));
        commit("setAppInfo", res.data);
        resolve(res.data);
      });
    },
  },
  getters: {
    isLoggedIn(state) {
      return Boolean(state.token && state.userInfo);
    },
  },
});

export default store;
